import React, { useCallback, useEffect, useState } from "react";

import API from "./api";

import Sidebar from "./components/Sidebar";
import PaymentTable from "./components/PaymentTable";
import TemplatePage from "./components/TemplatePage";
import DirectoryPage from "./components/DirectoryPage";
import UsersPage from "./components/UsersPage";
import ReportsPage from "./components/ReportsPage";
import ContractorsPage from "./components/ContractorsPage";
import LoginPage from "./components/LoginPage";
import CompaniesPage from "./components/CompaniesPage";
import PersonalMenusPage from "./components/PersonalMenusPage";
import LineItemsForm from "./components/LineItemsForm";
import BalancesBlock from "./components/BalancesBlock";
import BalancesSettings from "./components/BalancesSettings";

import { PAGES } from "./pages";
import { DEFAULT_WIDGETS, getPaymentValue } from "./reportWidgets";
import { REGISTER_PAGE_ID } from "./balances";

import "./index.css";

const defaultTemplateColumns = [
  { id: "branch", key: "branch", label: "Филиал", format: "text", required: false },
  { id: "contractor", key: "contractor", label: "Контрагент", format: "text", required: false },
  { id: "amount", key: "amount", label: "Сумма", format: "number", required: false },
  { id: "purpose", key: "purpose", label: "Назначение", format: "text", required: false },
  { id: "initiator", key: "initiator", label: "Инициатор", format: "text", required: false }
];

const builtinRoleLabels = {
  SYSTEM_ADMIN: "Владелец платформы",
  ADMIN: "Админ"
};

const defaultPaymentActions = [
  { id: "created", label: "Создано", status: "CREATED", showInMenu: false },
  { id: "approved", label: "Одобрено", status: "APPROVED", showInMenu: false },
  { id: "paid", label: "Оплачено", status: "PAID", showInMenu: false },
  { id: "rejected", label: "Отказано", status: "REJECTED", showInMenu: false }
];

const REGISTER_PAGE = "Реестр платежей";

const normalizeActionColumns = (columns = []) =>
  columns.map((column) => ({
    ...column,
    optionsText: column.optionsText ||
      (column.options || []).map((option) => option.label).join("\n"),
    options: (column.options || []).map((option) => ({
      id: option.id,
      label: option.label,
      value: option.value || option.label
    }))
  }));

const mapUser = (user) => ({
  ...user,
  role_label: user.role_label || builtinRoleLabels[user.role] || user.role || ""
});

const SETTINGS_PAGE_KEYS = ["settings", "template", "directory", "personal_menus"];

const firstAvailablePage = (pages = []) => {
  const page = PAGES.find((item) => pages.includes(item.key));
  return page ? page.label : REGISTER_PAGE;
};

// Платёж соответствует всем условиям меню (значение поля берётся через getPaymentValue,
// поэтому работает и в двухуровневом режиме).
const matchesCustomMenu = (payment, conditions, templateColumns) =>
  (conditions || []).every((condition) => {
    const column = templateColumns.find((item) => item.id === condition.fieldId);
    const actualValue = column
      ? getPaymentValue(payment, column.key || column.id, templateColumns)
      : payment.extra_fields?.[condition.fieldId];
    const expectedValue = condition.value;

    if (expectedValue === "" || expectedValue === null || expectedValue === undefined) {
      return true;
    }

    if (column?.format === "number" || column?.key === "amount") {
      const actualNumber = Number(actualValue);
      const expectedNumber = Number(expectedValue);

      if (Number.isNaN(actualNumber) || Number.isNaN(expectedNumber)) return false;
      if (condition.operator === "greater_than") return actualNumber > expectedNumber;
      if (condition.operator === "greater_or_equal") return actualNumber >= expectedNumber;
      if (condition.operator === "less_than") return actualNumber < expectedNumber;
      if (condition.operator === "less_or_equal") return actualNumber <= expectedNumber;
      if (condition.operator === "not_equals") return actualNumber !== expectedNumber;
      return actualNumber === expectedNumber;
    }

    if (column?.format === "date") {
      const actualDate = new Date(actualValue).getTime();
      const expectedDate = new Date(expectedValue).getTime();

      if (Number.isNaN(actualDate) || Number.isNaN(expectedDate)) return false;
      if (condition.operator === "before") return actualDate < expectedDate;
      if (condition.operator === "after") return actualDate > expectedDate;
      if (condition.operator === "on_or_before") return actualDate <= expectedDate;
      if (condition.operator === "on_or_after") return actualDate >= expectedDate;
      return actualDate === expectedDate;
    }

    const actualText = String(actualValue ?? "").toLocaleLowerCase();
    const expectedText = String(expectedValue).toLocaleLowerCase();

    if (condition.operator === "contains") return actualText.includes(expectedText);
    if (condition.operator === "not_contains") return !actualText.includes(expectedText);
    if (condition.operator === "not_equals") return actualText !== expectedText;
    return actualText === expectedText;
  });

function App() {
  const [payments, setPayments] = useState([]);
  const [activePage, setActivePage] = useState(REGISTER_PAGE);
  const [templateColumns, setTemplateColumns] = useState(defaultTemplateColumns);
  const [paymentActions, setPaymentActions] = useState(defaultPaymentActions);
  const [actionColumns, setActionColumns] = useState([]);
  const [customMenus, setCustomMenus] = useState([]);
  const [directories, setDirectories] = useState([]);
  const [templateMode, setTemplateMode] = useState("single");
  const [summaryRules, setSummaryRules] = useState({});
  const [groupBy, setGroupBy] = useState("");
  const [reportWidgets, setReportWidgets] = useState(DEFAULT_WIDGETS());
  const [balances, setBalances] = useState({ date: "", accounts: [], deductions: [], pages: [] });
  const [counterparties, setCounterparties] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [loginError, setLoginError] = useState("");

  const [branch, setBranch] = useState("");
  const [contractor, setContractor] = useState("");
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [initiator, setInitiator] = useState("");
  const [extraFields, setExtraFields] = useState({});

  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const loadUsers = useCallback(async () => {
    const res = await API.get("/session/users");
    const availableUsers = res.data.map(mapUser);

    setUsers(availableUsers);
  }, []);

  const loadRoles = useCallback(async () => {
    if (!currentUser?.pages?.includes("users")) {
      setRoles([]);
      return;
    }

    const res = await API.get("/roles");
    setRoles(res.data);
  }, [currentUser]);

  const loadCompanies = async () => {
    const res = await API.get("/companies");
    setCompanies(res.data);
  };

  const loadPayments = async () => {
    const res = await API.get("/payments");
    setPayments(res.data);
  };

  const loadSettings = async () => {
    const res = await API.get("/settings");

    if (res.data.template_columns) {
      setTemplateColumns(res.data.template_columns);
    }

    setPaymentActions(res.data.payment_actions || defaultPaymentActions);
    setActionColumns(normalizeActionColumns(res.data.action_columns || []));
    setCustomMenus(res.data.custom_menus || []);
    setDirectories(res.data.directories || []);
    setTemplateMode(res.data.template_mode || "single");
    setSummaryRules(res.data.summary_rules || {});
    setGroupBy(res.data.group_by || "");
    setReportWidgets(res.data.report_widgets?.length ? res.data.report_widgets : DEFAULT_WIDGETS());
    setBalances(res.data.balances || { date: "", accounts: [], deductions: [], pages: [] });
    setSettingsLoaded(true);
  };

  const loadCounterparties = useCallback(async () => {
    const pages = currentUser?.pages || [];

    if (!pages.includes("payments") && !pages.includes("counterparties")) {
      setCounterparties([]);
      return;
    }

    const res = await API.get("/counterparties");
    setCounterparties(res.data);
  }, [currentUser]);

  const saveSettings = async (
    nextTemplateColumns,
    nextDirectories,
    nextPaymentActions,
    nextActionColumns,
    nextCustomMenus,
    nextTemplateMode,
    nextSummaryRules,
    nextGroupBy,
    nextReportWidgets,
    nextBalances
  ) => {
    await API.put("/settings", {
      template_columns: nextTemplateColumns,
      directories: nextDirectories,
      payment_actions: nextPaymentActions,
      action_columns: nextActionColumns,
      custom_menus: nextCustomMenus,
      template_mode: nextTemplateMode,
      summary_rules: nextSummaryRules,
      group_by: nextGroupBy,
      report_widgets: nextReportWidgets,
      balances: nextBalances
    });
  };

  useEffect(() => {
    const loadSession = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        setSessionChecked(true);
        return;
      }

      try {
        const res = await API.get("/session/me");
        setCurrentUser(mapUser(res.data));
      } catch (error) {
        localStorage.removeItem("authToken");
        setCurrentUser(null);
      } finally {
        setSessionChecked(true);
      }
    };

    loadSession();
  }, []);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    setSettingsLoaded(false);

    if (currentUser.role === "SYSTEM_ADMIN") {
      setActivePage("Компании");
      loadCompanies();
      loadUsers();
      return;
    }

    if (currentUser.pages?.includes("payments")) {
      loadPayments();
    }

    loadSettings();
    loadCounterparties();
    loadUsers();
    loadRoles();

    if (currentUser.role !== "ADMIN") {
      setActivePage(firstAvailablePage(currentUser.pages));
    }
  }, [currentUser, loadCounterparties, loadUsers, loadRoles]);

  const canEditSettings = (currentUser?.pages || []).some((page) =>
    SETTINGS_PAGE_KEYS.includes(page)
  );

  useEffect(() => {
    if (!settingsLoaded || !canEditSettings) {
      return;
    }

    saveSettings(templateColumns, directories, paymentActions, actionColumns, customMenus, templateMode, summaryRules, groupBy, reportWidgets, balances);
  }, [templateColumns, directories, paymentActions, actionColumns, customMenus, templateMode, summaryRules, groupBy, reportWidgets, balances, settingsLoaded, canEditSettings]);

  const login = async (username, password) => {
    try {
      const res = await API.post("/login", { username, password });
      const user = mapUser(res.data.user);

      localStorage.setItem("authToken", res.data.access_token);
      setLoginError("");
      setCurrentUser(user);
      setActivePage(
        user.role === "SYSTEM_ADMIN" ? "Компании" : firstAvailablePage(user.pages)
      );
    } catch (error) {
      setLoginError(error.response?.data?.detail || "Не удалось войти");
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    setCurrentUser(null);
    setActivePage(REGISTER_PAGE);
    setPayments([]);
    setUsers([]);
    setRoles([]);
    setCompanies([]);
    setCounterparties([]);
  };

  const addUser = async (name, role, username, password) => {
    try {
      await API.post("/users", { name, role, username, password });
      await loadUsers();
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось создать пользователя");
    }
  };

  const updateUserRole = async (userId, role, username, password) => {
    if (role !== "ADMIN") {
      await API.put(`/users/${userId}/role`, { role });
    }
    if (username !== undefined) {
      await API.put(`/users/${userId}/credentials`, { username, password: password || null });
    }
    await loadUsers();
  };

  const deleteUser = async (userId) => {
    await API.delete(`/users/${userId}`);
    await loadUsers();
    setUserToDelete(null);
  };

  const addRole = async (name, pages) => {
    try {
      await API.post("/roles", { name, pages });
      await loadRoles();
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось создать роль");
    }
  };

  const updateRole = async (roleId, name, pages) => {
    try {
      await API.put(`/roles/${roleId}`, { name, pages });
      await loadRoles();
      await loadUsers();
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось сохранить роль");
    }
  };

  const removeRole = async (roleId) => {
    try {
      await API.delete(`/roles/${roleId}`);
      await loadRoles();
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось удалить роль");
    }
  };

  const addCompany = async (payload) => {
    await API.post("/companies", payload);
    await loadCompanies();
  };

  const deleteCompany = async (companyId) => {
    await API.delete(`/companies/${companyId}`);
    await loadCompanies();
  };

  const addCounterparty = async (name) => {
    await API.post("/counterparties", { name });
    await loadCounterparties();
  };

  const removeCounterparty = async (name) => {
    try {
      await API.delete("/counterparties", { data: { name } });
      await loadCounterparties();
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось удалить контрагента");
    }
  };

  const getFieldValue = (column) => {
    if (column.key === "branch") return branch;
    if (column.key === "contractor") return contractor;
    if (column.key === "amount") return amount;
    if (column.key === "purpose") return purpose;
    if (column.key === "initiator") return initiator;

    return extraFields[column.id] || "";
  };

  const setFieldValue = (column, value) => {
    if (column.key === "branch") setBranch(value);
    else if (column.key === "contractor") setContractor(value);
    else if (column.key === "amount") setAmount(value);
    else if (column.key === "purpose") setPurpose(value);
    else if (column.key === "initiator") setInitiator(value);
    else setExtraFields((current) => ({
      ...current,
      [column.id]: value
    }));
  };

  const clearPaymentForm = () => {
    setBranch("");
    setContractor("");
    setAmount("");
    setPurpose("");
    setInitiator("");
    setExtraFields({});
  };

  const createPayment = async () => {
    const missingColumns = templateColumns
      .filter((column) => column.required && String(getFieldValue(column)).trim() === "")
      .map((column) => column.label);

    if (missingColumns.length > 0) {
      setMessage(`Не заполнены обязательные столбцы: ${missingColumns.join(", ")}`);
      return;
    }

    const paymentAmount = Number(amount);

    if (templateColumns.some((column) => column.key === "amount") &&
      amount.trim() !== "" &&
      Number.isNaN(paymentAmount)) {
      setMessage("Прошу ввести цифровое значение в поле Сумма");
      return;
    }

    const invalidNumberColumn = templateColumns.find((column) =>
      column.format === "number" &&
      column.key !== "amount" &&
      String(getFieldValue(column)).trim() !== "" &&
      Number.isNaN(Number(getFieldValue(column)))
    );

    if (invalidNumberColumn) {
      setMessage(`Прошу ввести цифровое значение в поле ${invalidNumberColumn.label}`);
      return;
    }

    const activeExtraFields = templateColumns
      .filter((column) => !column.key)
      .reduce((fields, column) => ({
        ...fields,
        [column.id]: extraFields[column.id] || ""
      }), {});

    await API.post("/payments", {
      branch,
      contractor,
      amount: Number.isNaN(paymentAmount) ? 0 : paymentAmount,
      purpose,
      initiator,
      extra_fields: activeExtraFields
    });

    clearPaymentForm();
    loadPayments();
    loadCounterparties();
  };

  const createTwoLevelPayment = async (items) => {
    if (!items || items.length === 0) {
      setMessage("Добавьте хотя бы одну строку");
      return false;
    }

    try {
      await API.post("/payments", { items });
      loadPayments();
      loadCounterparties();
      return true;
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось создать платёж");
      return false;
    }
  };

  const updatePaymentField = async (id, fieldId, value) => {
    await API.put(`/payments/${id}/field`, { field_id: fieldId, value });
    loadPayments();
  };

  const deletePayment = async (id) => {
    await API.delete(`/payments/${id}`);
    loadPayments();
    setPaymentToDelete(null);
  };

  const uploadExcel = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const formData = new FormData();

    formData.append("file", file);

    try {
      const res = await API.post("/upload-excel", formData);

      await loadPayments();
      await loadCounterparties();
      setMessage(`Добавлено платежей из Excel: ${res.data.added_count}`);
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось загрузить Excel файл");
    } finally {
      e.target.value = "";
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await API.get("/payment-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "Шаблон_реестра_платежей.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setMessage(error.response?.data?.detail || "Не удалось скачать шаблон Excel");
    }
  };

  const actionMenuItems = customMenus
    .filter((menu) => (menu.userIds || []).includes(currentUser?.id))
    .map((menu) => ({
      id: menu.id,
      label: menu.label,
      conditions: menu.conditions || [],
      tabs: menu.tabs || []
    }));
  const activeActionPage = actionMenuItems.find((item) => item.label === activePage);
  const isPaymentPage = activePage === REGISTER_PAGE || Boolean(activeActionPage);
  const currentPageId = activePage === REGISTER_PAGE ? REGISTER_PAGE_ID : activeActionPage?.id;
  const showBalances = (balances.pages || []).includes(currentPageId);

  // На странице персонального меню платежи фильтруются по общим условиям меню.
  const menuFilteredPayments = activeActionPage
    ? payments.filter((payment) => matchesCustomMenu(payment, activeActionPage.conditions, templateColumns))
    : payments;

  // Вкладки меню — у каждой свой набор условий.
  const menuTabs = activeActionPage?.tabs || [];
  const effectiveTabId = menuTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : (menuTabs[0]?.id || "");
  const activeTabObj = menuTabs.find((tab) => tab.id === effectiveTabId);
  const displayedPayments = activeTabObj
    ? menuFilteredPayments.filter((payment) =>
        matchesCustomMenu(payment, activeTabObj.conditions, templateColumns))
    : menuFilteredPayments;
  const userPages = currentUser?.pages || [];
  const hasPage = (key) => userPages.includes(key);
  const isSystemAdmin = currentUser?.role === "SYSTEM_ADMIN";
  const canViewPayments = hasPage("payments");
  const canViewReports = hasPage("reports");
  const canViewCounterparties = hasPage("counterparties");
  const canAddCounterparties = hasPage("counterparties");
  const canCreatePayment = hasPage("payments");
  const canManageActions = hasPage("payments");
  const canDeletePayment = hasPage("payments");

  const addTemplateColumn = () => {
    const id = `extra_${Date.now()}`;

    setTemplateColumns((current) => [
      ...current,
      {
        id,
        label: "Новый столбец",
        format: "text",
        required: false
      }
    ]);
  };

  const updateTemplateColumn = (id, field, value) => {
    setTemplateColumns((current) =>
      current.map((column) =>
        column.id === id ? { ...column, [field]: value } : column
      )
    );
  };

  const removeTemplateColumn = (id) => {
    setTemplateColumns((current) =>
      current.filter((column) => column.id !== id)
    );
  };

  const clearTemplate = () => {
    setTemplateColumns([]);
    clearPaymentForm();
  };

  const moveTemplateColumn = (id, direction) => {
    setTemplateColumns((current) => {
      const index = current.findIndex((column) => column.id === id);
      const targetIndex = index + direction;

      if (index < 0 || targetIndex < 0 || targetIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [column] = next.splice(index, 1);
      next.splice(targetIndex, 0, column);

      return next;
    });
  };

  const updateSummaryRule = (columnId, rule) => {
    setSummaryRules((current) => ({ ...current, [columnId]: rule }));
  };

  const updateBalances = (patch) =>
    setBalances((current) => ({ ...current, ...patch }));

  const addReportWidget = (widget) =>
    setReportWidgets((current) => [...current, widget]);

  const updateReportWidget = (id, patch) =>
    setReportWidgets((current) =>
      current.map((widget) => (widget.id === id ? { ...widget, ...patch } : widget))
    );

  const removeReportWidget = (id) =>
    setReportWidgets((current) => current.filter((widget) => widget.id !== id));

  const moveReportWidget = (id, direction) =>
    setReportWidgets((current) => {
      const index = current.findIndex((widget) => widget.id === id);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= current.length) return current;
      const next = [...current];
      const [widget] = next.splice(index, 1);
      next.splice(target, 0, widget);
      return next;
    });

  const launchTemplate = () => {
    clearPaymentForm();
    setActivePage(REGISTER_PAGE);
  };

  const addPaymentAction = () => {
    const id = `action_${Date.now()}`;

    setPaymentActions((current) => [
      ...current,
      {
        id,
        label: "Новое действие",
        status: `ACTION_${Date.now()}`,
        showInMenu: true
      }
    ]);
  };

  const updatePaymentAction = (id, field, value) => {
    setPaymentActions((current) =>
      current.map((action) => {
        if (action.id !== id) {
          return action;
        }

        if (field === "label") {
          return {
            ...action,
            label: value,
            status: action.status?.startsWith("ACTION_")
              ? value.trim() || action.status
              : action.status
          };
        }

        return { ...action, [field]: value };
      })
    );
  };

  const removePaymentAction = (id) => {
    setPaymentActions((current) =>
      current.filter((action) => action.id !== id)
    );
  };

  const addActionColumn = () => {
    const id = `action_column_${Date.now()}`;

    setActionColumns((current) => [
      ...current,
      {
        id,
        label: "Новый столбец",
        optionsText: "Вариант 1\nВариант 2",
        options: [
          {
            id: `${id}_option_1`,
            label: "Вариант 1",
            value: "Вариант 1"
          },
          {
            id: `${id}_option_2`,
            label: "Вариант 2",
            value: "Вариант 2"
          }
        ]
      }
    ]);
  };

  const normalizeActionOptions = (columnId, optionsText, currentOptions = []) => {
    return optionsText
      .split("\n")
      .map((label) => label.trim())
      .filter(Boolean)
      .map((label, index) => {
        const existing = currentOptions.find((option) => option.label === label);

        return {
          id: existing?.id || `${columnId}_option_${index}_${Date.now()}`,
          label,
          value: label
        };
      });
  };

  const updateActionColumn = (id, field, value) => {
    setActionColumns((current) =>
      current.map((column) => {
        if (column.id !== id) {
          return column;
        }

        if (field === "optionsText") {
          return {
            ...column,
            optionsText: value,
            options: normalizeActionOptions(column.id, value, column.options)
          };
        }

        return { ...column, [field]: value };
      })
    );
  };

  const removeActionColumn = (id) => {
    setActionColumns((current) =>
      current.filter((column) => column.id !== id)
    );
  };

  const createDefaultCondition = () => ({
    id: `condition_${Date.now()}_${Math.random().toString(16).slice(2)}`,
    fieldId: templateColumns[0]?.id || actionColumns[0]?.id || "",
    operator: "equals",
    value: ""
  });

  const addCustomMenu = () => {
    setCustomMenus((current) => [
      ...current,
      {
        id: `custom_menu_${Date.now()}`,
        label: "Новое меню",
        userIds: currentUser?.id ? [currentUser.id] : [],
        conditions: [createDefaultCondition()]
      }
    ]);
  };

  const updateCustomMenu = (menuId, field, value) => {
    setCustomMenus((current) =>
      current.map((menu) =>
        menu.id === menuId ? { ...menu, [field]: value } : menu
      )
    );
  };

  const removeCustomMenu = (menuId) => {
    setCustomMenus((current) => current.filter((menu) => menu.id !== menuId));
    setActivePage(REGISTER_PAGE);
  };

  const addCustomMenuCondition = (menuId) => {
    setCustomMenus((current) =>
      current.map((menu) =>
        menu.id === menuId
          ? { ...menu, conditions: [...(menu.conditions || []), createDefaultCondition()] }
          : menu
      )
    );
  };

  const updateCustomMenuCondition = (menuId, conditionId, patch) => {
    setCustomMenus((current) =>
      current.map((menu) =>
        menu.id === menuId
          ? {
              ...menu,
              conditions: (menu.conditions || []).map((condition) =>
                condition.id === conditionId ? { ...condition, ...patch } : condition
              )
            }
          : menu
      )
    );
  };

  const removeCustomMenuCondition = (menuId, conditionId) => {
    setCustomMenus((current) =>
      current.map((menu) =>
        menu.id === menuId
          ? {
              ...menu,
              conditions: (menu.conditions || []).filter((condition) => condition.id !== conditionId)
            }
          : menu
      )
    );
  };

  const addDirectory = () => {
    setDirectories((current) => [
      ...current,
      {
        id: `directory_${Date.now()}`,
        name: "Новый справочник",
        values: "",
        saved: false,
        savedName: "",
        savedValues: ""
      }
    ]);
  };

  const updateDirectory = (id, field, value) => {
    setDirectories((current) =>
      current.map((directory) =>
        directory.id === id ? { ...directory, [field]: value } : directory
      )
    );
  };

  const saveDirectory = (id) => {
    setDirectories((current) =>
      current.map((directory) =>
        directory.id === id
          ? {
              ...directory,
              saved: true,
              savedName: directory.name,
              savedValues: directory.values
            }
          : directory
      )
    );
  };

  const removeDirectory = (id) => {
    setDirectories((current) =>
      current.filter((directory) => directory.id !== id)
    );

    setTemplateColumns((current) =>
      current.map((column) =>
        column.format === `directory:${id}`
          ? { ...column, format: "text" }
          : column
      )
    );
  };

  const getDirectoryByFormat = (format) => {
    if (!format.startsWith("directory:")) {
      return null;
    }

    const directoryId = format.replace("directory:", "");

    return directories.find((directory) =>
      directory.id === directoryId && directory.saved
    ) || null;
  };

  const getColumnOptions = (column) => {
    if (column.key === "contractor" || column.format === "counterparty") {
      return counterparties;
    }

    const directory = getDirectoryByFormat(column.format);
    return directory ? getDirectoryValues(directory) : null;
  };

  const getDirectoryValues = (directory) => {
    if (!directory) {
      return [];
    }

    return directory.savedValues
      .split("\n")
      .map((value) => value.trim())
      .filter(Boolean);
  };

  const renderTemplateInput = (column) => {
    const directory = getDirectoryByFormat(column.format);
    const isContractorColumn = column.key === "contractor";

    if (isContractorColumn || column.format === "counterparty") {
      const value = isContractorColumn ? contractor : getFieldValue(column);
      const handleChange = (next) =>
        isContractorColumn ? setContractor(next) : setFieldValue(column, next);

      return (
        <select
          key={column.id}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
        >
          <option value="">
            {column.required ? `${column.label} *` : column.label}
          </option>

          {counterparties.map((counterparty) => (
            <option key={counterparty} value={counterparty}>
              {counterparty}
            </option>
          ))}
        </select>
      );
    }

    if (directory) {
      return (
        <select
          key={column.id}
          value={getFieldValue(column)}
          onChange={(e) => setFieldValue(column, e.target.value)}
        >
          <option value="">
            {column.required ? `${column.label} *` : column.label}
          </option>

          {getDirectoryValues(directory).map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        key={column.id}
        placeholder={column.required ? `${column.label} *` : column.label}
        value={getFieldValue(column)}
        onChange={(e) => setFieldValue(column, e.target.value)}
        type={column.format === "date" ? "date" : "text"}
        inputMode={column.format === "number" ? "decimal" : undefined}
      />
    );
  };

  if (!sessionChecked) {
    return null;
  }

  if (!currentUser) {
    return <LoginPage login={login} error={loginError} />;
  }

  return (
    <div className="app">
      <Sidebar
        activePage={activePage}
        setActivePage={setActivePage}
        currentUser={currentUser}
        paymentActions={paymentActions}
        actionMenuItems={actionMenuItems}
        logout={logout}
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((value) => !value)}
      />

      <div className="content">
        {isSystemAdmin && activePage === "Компании" && (
          <CompaniesPage
            companies={companies}
            addCompany={addCompany}
            deleteCompany={deleteCompany}
          />
        )}

        {canViewPayments && isPaymentPage && (
          <>
            <h1>{activePage}</h1>

            {showBalances && (
              <BalancesBlock balances={balances} payments={payments} templateColumns={templateColumns} />
            )}

            {menuTabs.length > 0 && (
              <div className="menu-tabs">
                {menuTabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`menu-tab ${tab.id === effectiveTabId ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label || "Вкладка"}
                  </button>
                ))}
              </div>
            )}

            {activePage === REGISTER_PAGE && canCreatePayment && templateMode === "two_level" && (
              <LineItemsForm
                columns={templateColumns}
                getColumnOptions={getColumnOptions}
                onCreate={createTwoLevelPayment}
              />
            )}

            {activePage === REGISTER_PAGE && canCreatePayment && templateMode !== "two_level" && (
              <div className="filters">
                {templateColumns.map(renderTemplateInput)}

                <button onClick={createPayment}>
                  Создать платеж
                </button>
              </div>
            )}

            {activePage === REGISTER_PAGE && canCreatePayment && (
              <div className="filters register-tools">
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={uploadExcel}
                />

                <button
                  className="download-template-btn"
                  onClick={downloadTemplate}
                >
                  Скачать шаблон Excel
                </button>
              </div>
            )}

            <PaymentTable
              key={currentPageId || "register"}
              viewKey={currentPageId || "register"}
              payments={displayedPayments}
              templateColumns={templateColumns}
              actionColumns={actionColumns}
              updatePaymentField={updatePaymentField}
              deletePayment={setPaymentToDelete}
              showActions={canManageActions}
              canDelete={canDeletePayment}
              templateMode={templateMode}
              summaryRules={summaryRules}
              sections={activeTabObj?.sections || []}
              matchConditions={(payment, conditions) =>
                matchesCustomMenu(payment, conditions, templateColumns)}
            />
          </>
        )}

        {hasPage("template") && activePage === "Шаблон" && (
          <TemplatePage
            templateColumns={templateColumns}
            directories={directories.filter((directory) => directory.saved)}
            paymentActions={paymentActions}
            actionColumns={actionColumns}
            updateTemplateColumn={updateTemplateColumn}
            addTemplateColumn={addTemplateColumn}
            clearTemplate={clearTemplate}
            removeTemplateColumn={removeTemplateColumn}
            moveTemplateColumn={moveTemplateColumn}
            launchTemplate={launchTemplate}
            addPaymentAction={addPaymentAction}
            updatePaymentAction={updatePaymentAction}
            removePaymentAction={removePaymentAction}
            addActionColumn={addActionColumn}
            updateActionColumn={updateActionColumn}
            removeActionColumn={removeActionColumn}
            templateMode={templateMode}
            setTemplateMode={setTemplateMode}
            summaryRules={summaryRules}
            updateSummaryRule={updateSummaryRule}
          />
        )}

        {hasPage("personal_menus") && activePage === "Персональные меню" && (
          <PersonalMenusPage
            customMenus={customMenus}
            templateColumns={templateColumns}
            actionColumns={actionColumns}
            directories={directories}
            counterparties={counterparties}
            users={users}
            addCustomMenu={addCustomMenu}
            updateCustomMenu={updateCustomMenu}
            removeCustomMenu={removeCustomMenu}
            addCustomMenuCondition={addCustomMenuCondition}
            updateCustomMenuCondition={updateCustomMenuCondition}
            removeCustomMenuCondition={removeCustomMenuCondition}
          />
        )}

        {hasPage("directory") && activePage === "Справочник" && (
          <DirectoryPage
            directories={directories}
            addDirectory={addDirectory}
            updateDirectory={updateDirectory}
            saveDirectory={saveDirectory}
            removeDirectory={removeDirectory}
          />
        )}

        {hasPage("users") && activePage === "Пользователи" && (
          <UsersPage
            users={users}
            currentUser={currentUser}
            roles={roles}
            pages={PAGES}
            addUser={addUser}
            updateUserRole={updateUserRole}
            deleteUser={setUserToDelete}
            addRole={addRole}
            updateRole={updateRole}
            removeRole={removeRole}
          />
        )}

        {canViewCounterparties && activePage === "Контрагенты" && (
          <ContractorsPage
            counterparties={counterparties}
            payments={payments}
            addCounterparty={addCounterparty}
            removeCounterparty={removeCounterparty}
            canAdd={canAddCounterparties}
          />
        )}

        {canViewReports && activePage === "Отчеты" && (
          <ReportsPage
            payments={payments}
            templateColumns={templateColumns}
            paymentActions={paymentActions}
            actionColumns={actionColumns}
            reportWidgets={reportWidgets}
            isAdmin={currentUser?.role === "ADMIN"}
            addReportWidget={addReportWidget}
            updateReportWidget={updateReportWidget}
            removeReportWidget={removeReportWidget}
            moveReportWidget={moveReportWidget}
          />
        )}

        {hasPage("settings") && activePage === "Настройки" && (
          <BalancesSettings
            balances={balances}
            templateColumns={templateColumns}
            actionColumns={actionColumns}
            customMenus={customMenus}
            updateBalances={updateBalances}
          />
        )}

        {!isPaymentPage &&
          activePage !== "Компании" &&
          activePage !== "Шаблон" &&
          activePage !== "Персональные меню" &&
          activePage !== "Справочник" &&
          activePage !== "Пользователи" &&
          activePage !== "Контрагенты" &&
          activePage !== "Настройки" &&
          activePage !== "Отчеты" && (
          <div>
            <h1>{activePage}</h1>
            <p>Раздел пока не настроен.</p>
          </div>
        )}

        {message && (
          <div className="msgbox-overlay">
            <div className="msgbox">
              <p>{message}</p>
              <button onClick={() => setMessage("")}>
                OK
              </button>
            </div>
          </div>
        )}

        {paymentToDelete && (
          <div className="msgbox-overlay">
            <div className="msgbox">
              <p>Вы уверены?</p>

              <div className="msgbox-actions">
                <button onClick={() => deletePayment(paymentToDelete)}>
                  Да
                </button>

                <button
                  className="secondary-btn"
                  onClick={() => setPaymentToDelete(null)}
                >
                  Нет
                </button>
              </div>
            </div>
          </div>
        )}

        {userToDelete && (
          <div className="msgbox-overlay">
            <div className="msgbox">
              <p>Вы уверены?</p>

              <div className="msgbox-actions">
                <button onClick={() => deleteUser(userToDelete)}>
                  Да
                </button>

                <button
                  className="secondary-btn"
                  onClick={() => setUserToDelete(null)}
                >
                  Нет
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
